import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../config/api';
import { 
  UserGroupIcon, 
  ExclamationTriangleIcon,
  TrophyIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

interface ClientActivityPageProps {
  user: any;
}

interface ClientActivity {
  id: string;
  name: string;
  email: string;
  lastMealDate: string;
  daysInactive: number;
  mealsThisWeek: number;
  avgMealsPerDay: number;
  goalProgress: number;
  status: 'active' | 'attention' | 'inactive';
}

interface ActivitySummary {
  totalClients: number;
  activeClients: number;
  needsAttention: number;
  inactiveClients: number;
  avgMealsPerClient: number;
  topPerformer: {
    name: string;
    mealsThisWeek: number;
  };
}

export const ClientActivityPage: React.FC<ClientActivityPageProps> = ({ user }) => {
  const [timeframe, setTimeframe] = useState('7d');

  // Get coach clients
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['coach-clients'],
    queryFn: () => apiClient.getCoachClients(),
    enabled: !!user?.id,
  });

  // Get client activity data
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['client-activity', timeframe],
    queryFn: async () => {
      const clients = clientsData?.clients || [];
      if (clients.length === 0) return { activities: [], summary: {} };

      // Process each client's activity
      const activities = await Promise.all(
        clients.map(async (client: any) => {
          try {
            // Get recent meals for this client using portfolio endpoint
            // Use the same endpoint pattern that works in your logs: /api/meals/portfolio?client_id=X
            const portfolioData = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/meals/portfolio?days=${timeframe === '7d' ? 7 : timeframe === '14d' ? 14 : 30}&client_id=${client.client_id}`, {
              headers: { 
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
              },
              credentials: 'include'
            });
            const mealsData = await portfolioData.json();
            const meals = mealsData?.portfolio || [];

            // Calculate activity metrics
            const today = new Date();
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            
            const recentMeals = meals.filter((meal: any) => {
              const mealDate = new Date(meal.meal_date || meal.timestamp);
              return mealDate >= weekAgo;
            });

            const lastMeal = meals.length > 0 ? 
              new Date(Math.max(...meals.map((m: any) => new Date(m.meal_date || m.timestamp).getTime()))) :
              null;

            const daysInactive = lastMeal ? 
              Math.floor((today.getTime() - lastMeal.getTime()) / (1000 * 60 * 60 * 24)) : 999;

            // Determine status
            let status: 'active' | 'attention' | 'inactive' = 'active';
            if (daysInactive > 7) status = 'inactive';
            else if (daysInactive > 3 || recentMeals.length < 7) status = 'attention';

            return {
              id: client.client_id,
              name: client.full_name || client.email,
              email: client.email,
              lastMealDate: lastMeal ? lastMeal.toISOString().split('T')[0] : 'Never',
              daysInactive,
              mealsThisWeek: recentMeals.length,
              avgMealsPerDay: recentMeals.length / 7,
              goalProgress: Math.min(100, (recentMeals.length / 21) * 100), // Assuming 3 meals/day target
              status
            } as ClientActivity;
          } catch (error) {
            return {
              id: client.client_id,
              name: client.full_name || client.email,
              email: client.email,
              lastMealDate: 'Error',
              daysInactive: 999,
              mealsThisWeek: 0,
              avgMealsPerDay: 0,
              goalProgress: 0,
              status: 'inactive'
            } as ClientActivity;
          }
        })
      );

      // Calculate summary stats
      const summary = {
        totalClients: activities.length,
        activeClients: activities.filter(a => a.status === 'active').length,
        needsAttention: activities.filter(a => a.status === 'attention').length,
        inactiveClients: activities.filter(a => a.status === 'inactive').length,
        avgMealsPerClient: activities.reduce((sum, a) => sum + a.mealsThisWeek, 0) / Math.max(activities.length, 1),
        topPerformer: activities.reduce((best, current) => 
          current.mealsThisWeek > best.mealsThisWeek ? current : best, 
          activities[0] || { name: 'None', mealsThisWeek: 0 }
        )
      };

      return { activities, summary };
    },
    enabled: !!clientsData?.clients && clientsData.clients.length > 0,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes (no auto-refresh)
  });

  const timeframeOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '14d', label: 'Last 14 Days' },
    { value: '30d', label: 'Last 30 Days' }
  ];

  if (clientsLoading || activityLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gray-200 rounded-lg h-32 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-200 rounded-lg h-24 animate-pulse" />
          ))}
        </div>
        <div className="bg-gray-200 rounded-lg h-64 animate-pulse" />
      </div>
    );
  }

  const activities: ClientActivity[] = activityData?.activities || [];
  const summary: ActivitySummary = (activityData?.summary as ActivitySummary) || {
    totalClients: 0,
    activeClients: 0,
    needsAttention: 0,
    inactiveClients: 0,
    avgMealsPerClient: 0,
    topPerformer: { name: 'None', mealsThisWeek: 0 }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-sm">
        <div className="px-6 py-8 sm:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-white">
                Client Activity Overview 👥
              </h1>
              <p className="mt-2 text-green-100">
                Monitor client engagement and identify who needs attention
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="bg-white/10 text-white rounded-lg px-3 py-2 text-sm border border-white/20"
              >
                {timeframeOptions.map(option => (
                  <option key={option.value} value={option.value} className="text-gray-900">
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="text-right">
                <div className="text-2xl font-bold text-white">
                  {summary.totalClients || 0}
                </div>
                <div className="text-green-100 text-sm">
                  total clients
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserGroupIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Clients</p>
              <p className="text-2xl font-semibold text-gray-900">{summary.activeClients || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Need Attention</p>
              <p className="text-2xl font-semibold text-gray-900">{summary.needsAttention || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avg Meals/Client</p>
              <p className="text-2xl font-semibold text-gray-900">
                {(summary.avgMealsPerClient || 0).toFixed(1)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrophyIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Top Performer</p>
              <p className="text-sm font-semibold text-gray-900 truncate">
                {summary.topPerformer?.name || 'None'}
              </p>
              <p className="text-xs text-gray-500">
                {summary.topPerformer?.mealsThisWeek || 0} meals
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* System Health (Simple) */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <HeartIcon className="h-5 w-5 text-green-500 mr-2" />
            <span className="text-sm font-medium text-gray-900">System Status</span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
            <span className="text-sm text-gray-600">All systems operational</span>
          </div>
        </div>
      </div>

      {/* Client Activity Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Client Activity Details</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Meal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Meals This Week
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Daily Average
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activities.map((activity) => (
                <tr key={activity.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{activity.name}</div>
                      <div className="text-sm text-gray-500">{activity.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      activity.status === 'active' ? 'bg-green-100 text-green-800' :
                      activity.status === 'attention' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {activity.status === 'active' ? 'Active' :
                       activity.status === 'attention' ? 'Needs Attention' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {activity.lastMealDate}
                    {activity.daysInactive > 0 && activity.daysInactive < 999 && (
                      <div className="text-xs text-gray-500">
                        {activity.daysInactive} days ago
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {activity.mealsThisWeek}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {activity.avgMealsPerDay.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className={`h-2 rounded-full ${
                            activity.goalProgress >= 80 ? 'bg-green-500' :
                            activity.goalProgress >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(100, activity.goalProgress)}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">
                        {Math.round(activity.goalProgress)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
            <h4 className="text-sm font-medium text-yellow-800">Clients Needing Attention</h4>
          </div>
          <p className="mt-1 text-sm text-yellow-700">
            {activities.filter(a => a.status === 'attention').length} clients haven't logged meals in 3+ days
          </p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <CalendarDaysIcon className="h-5 w-5 text-red-600 mr-2" />
            <h4 className="text-sm font-medium text-red-800">Inactive Clients</h4>
          </div>
          <p className="mt-1 text-sm text-red-700">
            {activities.filter(a => a.status === 'inactive').length} clients haven't logged meals in 7+ days
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <TrophyIcon className="h-5 w-5 text-green-600 mr-2" />
            <h4 className="text-sm font-medium text-green-800">High Performers</h4>
          </div>
          <p className="mt-1 text-sm text-green-700">
            {activities.filter(a => a.status === 'active' && a.mealsThisWeek >= 14).length} clients logging 2+ meals daily
          </p>
        </div>
      </div>
    </div>
  );
};